import { useState, useCallback } from "react";
import { QRCodeSVG } from "qrcode.react";
import { X, RefreshCw } from "lucide-react";
import { useEKYCManagerService } from "../modules/ekyc_manager/services/useEKYCManagerService";

const buildQrUrl = () => {
  const token = sessionStorage.getItem("ekycSessionToken");
  if (!token) return "";
  const base = window.location.origin + window.location.pathname;
  return `${base}?token=${token}`;
};

const DesktopQrModal = ({ open, onClose }) => {
  const { getStatus } = useEKYCManagerService();
  const [isChecking, setIsChecking] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  const validationID = sessionStorage.getItem("ekycValidationID");
  const redirectURL = sessionStorage.getItem("ekycRedirectURL");

  const handleCheckStatus = useCallback(async () => {
    if (!validationID || isChecking) return;

    setIsChecking(true);
    setStatusMessage("");

    try {
      const result = await getStatus(validationID);
      if (result.status === "success") {
        const validationStatus = result.data?.Status;
        const serverRedirectURL = result.data?.RedirectURL || redirectURL;
        if (validationStatus === "Success" || validationStatus === "In Review") {
          setStatusMessage("Verification completed! Redirecting...");
          if (serverRedirectURL) {
            setTimeout(() => window.location.replace(serverRedirectURL), 1500);
          }
        } else if (validationStatus === "Fail") {
          setStatusMessage("Verification failed. Please try again.");
        } else {
          setStatusMessage("Verification is still in progress.");
        }
      }
    } catch {
      setStatusMessage("Unable to check status. Please try again.");
    } finally {
      setIsChecking(false);
    }
  }, [validationID, isChecking, getStatus, redirectURL]);

  if (!open) return null;

  const qrUrl = buildQrUrl();

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "420px",
          background: "#ffffff",
          borderRadius: "16px",
          padding: "1.25rem",
          position: "relative",
          boxShadow: "0 10px 30px rgba(0, 0, 0, 0.25)",
        }}
      >
        <button
          type="button"
          onClick={() => onClose?.()}
          style={{
            position: "absolute",
            top: "0.75rem",
            right: "0.75rem",
            width: "40px",
            height: "40px",
            borderRadius: "9999px",
            border: "1px solid #e5e7eb",
            background: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <h2 style={{ fontSize: "1.125rem", fontWeight: 700, margin: 0, paddingRight: "3rem" }}>
          Continue on mobile
        </h2>
        <p style={{ fontSize: "0.875rem", color: "#4b5563", marginTop: "0.5rem" }}>
          Scan this QR code using your mobile phone to continue the verification.
        </p>

        <div
          style={{
            marginTop: "1rem",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <a
            href={qrUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: "0.75rem",
              borderRadius: "12px",
              border: "1px solid #e5e7eb",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <QRCodeSVG value={qrUrl} size={220} level="H" />
          </a>
        </div>

        {validationID && (
          <div style={{ marginTop: "1rem", textAlign: "center" }}>
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            <button
              type="button"
              onClick={handleCheckStatus}
              disabled={isChecking}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.625rem 1.25rem",
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                background: isChecking ? "#f3f4f6" : "#ffffff",
                cursor: isChecking ? "not-allowed" : "pointer",
                fontSize: "0.875rem",
                fontWeight: 500,
                color: "#374151",
              }}
            >
              <RefreshCw size={16} style={{ animation: isChecking ? "spin 1s linear infinite" : "none" }} />
              {isChecking ? "Checking..." : "I've completed verification"}
            </button>
            {statusMessage && (
              <p style={{ fontSize: "0.8125rem", color: "#6b7280", marginTop: "0.5rem" }}>
                {statusMessage}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DesktopQrModal;
